import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
} from "react";

import { SIDEBAR_EXPANDED_W } from "@/components/layout/sidebar/sidebar.constants";

import {
  SIDEBAR_DEFAULT_WIDTH_PX,
  SIDEBAR_MAX_WIDTH_PX,
  SIDEBAR_MIN_PAGE_WIDTH_PX,
  SIDEBAR_MIN_WIDTH_PX,
} from "../constants";
import { localGet, localSet } from "../utils/safe-storage";

/** Layout padding + the gaps between the layout's cards. */
const LAYOUT_CHROME_PX = 32;
/** Arrow-key resize step for the keyboard handle. */
const KEYBOARD_STEP_PX = 16;

/**
 * Widest the sidebar may get at this viewport: capped so that, even with the
 * main menu expanded, the page beside it keeps SIDEBAR_MIN_PAGE_WIDTH_PX.
 */
export const maxSidebarWidth = (viewportWidth: number): number =>
  Math.max(
    SIDEBAR_MIN_WIDTH_PX,
    Math.min(
      SIDEBAR_MAX_WIDTH_PX,
      viewportWidth -
        SIDEBAR_EXPANDED_W -
        SIDEBAR_MIN_PAGE_WIDTH_PX -
        LAYOUT_CHROME_PX,
    ),
  );

export const clampSidebarWidth = (width: number, viewportWidth: number) =>
  Math.round(
    Math.min(
      Math.max(width, SIDEBAR_MIN_WIDTH_PX),
      maxSidebarWidth(viewportWidth),
    ),
  );

const viewportWidth = () =>
  typeof window !== "undefined" ? window.innerWidth : 1280;

const readStoredWidth = (storageKey: string): number => {
  const stored = Number(localGet(storageKey));
  return Number.isFinite(stored) && stored > 0
    ? stored
    : SIDEBAR_DEFAULT_WIDTH_PX;
};

interface UseSidebarWidthArgs {
  storageKey: string;
  /** The handle sits on the inline-start edge, so RTL drags the other way. */
  isRtl: boolean;
}

/**
 * Width of the docked Maya sidebar: dragged from its inline-start edge (or
 * stepped with the arrow / Home / End keys on the focused handle) and
 * remembered across sessions.
 *
 * The remembered value is the user's preference; what is shown is that,
 * clamped to the current viewport — so briefly narrowing the window doesn't
 * permanently shrink the sidebar.
 */
export const useSidebarWidth = ({ storageKey, isRtl }: UseSidebarWidthArgs) => {
  const [preferred, setPreferred] = useState(() => readStoredWidth(storageKey));
  const [viewport, setViewport] = useState(viewportWidth);
  const [resizing, setResizing] = useState(false);
  const width = clampSidebarWidth(preferred, viewport);

  // Read by the pointer handlers, which outlive the render they started in.
  const widthRef = useRef(width);
  useLayoutEffect(() => {
    widthRef.current = width;
  }, [width]);

  const commit = useCallback(
    (w: number) => {
      setPreferred(w);
      localSet(storageKey, String(w));
    },
    [storageKey],
  );

  useEffect(() => {
    const onResize = () => setViewport(viewportWidth());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const startResize = useCallback(
    (e: ReactPointerEvent<HTMLElement>) => {
      if (e.button !== 0) return;
      e.preventDefault();
      // Keep receiving moves even if the pointer crosses an iframe or leaves
      // the window mid-drag.
      e.currentTarget.setPointerCapture?.(e.pointerId);
      const startX = e.clientX;
      const origin = widthRef.current;
      // Dragging the edge toward the page (left in LTR) grows the sidebar.
      const direction = isRtl ? 1 : -1;

      const prevUserSelect = document.body.style.userSelect;
      const prevCursor = document.body.style.cursor;
      document.body.style.userSelect = "none";
      document.body.style.cursor = "ew-resize";
      setResizing(true);

      const move = (ev: PointerEvent) => {
        ev.preventDefault();
        setPreferred(
          clampSidebarWidth(
            origin + (ev.clientX - startX) * direction,
            viewportWidth(),
          ),
        );
      };
      const up = () => {
        window.removeEventListener("pointermove", move);
        window.removeEventListener("pointerup", up);
        window.removeEventListener("pointercancel", up);
        document.body.style.userSelect = prevUserSelect;
        document.body.style.cursor = prevCursor;
        setResizing(false);
        commit(widthRef.current);
      };

      window.addEventListener("pointermove", move, { passive: false });
      window.addEventListener("pointerup", up);
      window.addEventListener("pointercancel", up);
    },
    [isRtl, commit],
  );

  const onResizeKeyDown = useCallback(
    (e: ReactKeyboardEvent) => {
      // Arrow keys move the edge: toward the page grows the sidebar.
      const grow = isRtl ? "ArrowRight" : "ArrowLeft";
      const shrink = isRtl ? "ArrowLeft" : "ArrowRight";
      let next: number;
      if (e.key === grow) next = widthRef.current + KEYBOARD_STEP_PX;
      else if (e.key === shrink) next = widthRef.current - KEYBOARD_STEP_PX;
      else if (e.key === "Home") next = SIDEBAR_MIN_WIDTH_PX;
      else if (e.key === "End") next = maxSidebarWidth(viewportWidth());
      else return;
      e.preventDefault();
      commit(clampSidebarWidth(next, viewportWidth()));
    },
    [isRtl, commit],
  );

  /** Back to the default width (double-click on the handle). */
  const resetWidth = useCallback(
    () => commit(clampSidebarWidth(SIDEBAR_DEFAULT_WIDTH_PX, viewportWidth())),
    [commit],
  );

  return {
    width,
    resizing,
    minWidth: SIDEBAR_MIN_WIDTH_PX,
    maxWidth: maxSidebarWidth(viewport),
    startResize,
    onResizeKeyDown,
    resetWidth,
  };
};
